import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { lastActivity, markActivity, trackActivity } from '../lib/activity';
import * as api from '../lib/api';
import { SESSION_EXPIRED_EVENT, setAccessToken } from '../lib/api';
import type { MfaChallenge, Session, User } from '../lib/types';

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

/** Why the last session ended, when it did not end by the user's choice. */
export type EndReason = 'idle' | null;

interface AuthState {
  status: AuthStatus;
  user: User | null;
  pcc: string;
  mustChangePassword: boolean;
  endReason: EndReason;
  /** Seconds until the idle sign-out, once it is less than a minute away. */
  idleSecondsLeft: number | null;
}

interface AuthContextValue extends AuthState {
  /** Resolves to a challenge instead of a session when the account uses two-step verification. */
  signIn: (userId: string, password: string, pcc: string) => Promise<Session | MfaChallenge>;
  verifyMfa: (mfaToken: string, code: string) => Promise<Session>;
  signOut: () => Promise<void>;
  /** Starts a session the user just obtained (sign-in, password change). */
  applySession: (session: Session) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const anonymousState: AuthState = {
  status: 'anonymous',
  user: null,
  pcc: '',
  mustChangePassword: false,
  endReason: null,
  idleSecondsLeft: null,
};

// How often the idle clock is checked, and when the warning starts.
const IDLE_CHECK_MS = 5_000;
const IDLE_WARNING_SECONDS = 60;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ ...anonymousState, status: 'loading' });
  const renewTimer = useRef<number | null>(null);
  // Seconds of inactivity that end the session; 0 means no limit.
  const idleTimeout = useRef(0);

  const clearRenewTimer = useCallback(() => {
    if (renewTimer.current !== null) {
      window.clearTimeout(renewTimer.current);
      renewTimer.current = null;
    }
  }, []);

  const isIdle = useCallback(
    () => idleTimeout.current > 0 && Date.now() - lastActivity() >= idleTimeout.current * 1000,
    [],
  );

  /** Ends the session after inactivity: the server forgets it too. */
  const endForIdle = useCallback(() => {
    clearRenewTimer();
    void api.logout().catch(() => undefined);
    setAccessToken(null);
    setState({ ...anonymousState, endReason: 'idle' });
  }, [clearRenewTimer]);

  const installSession = useCallback(
    (session: Session) => {
      setAccessToken(session.accessToken);
      idleTimeout.current = session.idleTimeout ?? 0;
      // No record at all (storage blocked, first load): start the clock now.
      if (lastActivity() === 0) markActivity();
      setState({
        status: 'authenticated',
        user: session.user,
        pcc: session.pcc ?? session.user.pcc ?? '',
        mustChangePassword: session.mustChangePassword,
        endReason: null,
        idleSecondsLeft: null,
      });

      // Renew a minute before the access token runs out, or before the
      // server's idle window does if that is sooner, but only while the user
      // is active: the server will not renew a session left idle.
      clearRenewTimer();
      const window_ = idleTimeout.current > 0 ? Math.min(session.expiresIn, idleTimeout.current) : session.expiresIn;
      const renewIn = Math.max((window_ - 60) * 1000, 30_000);
      renewTimer.current = window.setTimeout(() => {
        if (isIdle()) {
          endForIdle();
          return;
        }
        api
          .refreshSession()
          .then(installSession)
          .catch(() => {
            setAccessToken(null);
            setState(anonymousState);
          });
      }, renewIn);
    },
    [clearRenewTimer, endForIdle, isIdle],
  );

  // Signing in, setting up the authenticator or changing the password is
  // activity in itself.
  const applySession = useCallback(
    (session: Session) => {
      markActivity();
      installSession(session);
    },
    [installSession],
  );

  // Recover the session from the httpOnly refresh cookie on a hard reload. The
  // idle clock lives in localStorage, so a reload does not reset it.
  useEffect(() => {
    let cancelled = false;
    api
      .refreshSession()
      .then((session) => {
        if (!cancelled) installSession(session);
      })
      .catch(() => {
        if (!cancelled) setState(anonymousState);
      });
    return () => {
      cancelled = true;
    };
  }, [installSession]);

  useEffect(() => {
    const onExpired = () => {
      clearRenewTimer();
      // A 401 on a tab left idle reads better as the idle sign-out it is.
      setState({ ...anonymousState, endReason: isIdle() ? 'idle' : null });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [clearRenewTimer, isIdle]);

  useEffect(() => clearRenewTimer, [clearRenewTimer]);

  // While signed in: watch for activity, warn in the last minute, and sign
  // out once the idle limit passes. Checked again when the tab comes back to
  // the foreground, since timers stall while a laptop sleeps.
  const signedIn = state.status === 'authenticated';
  useEffect(() => {
    if (!signedIn) return undefined;
    const stopTracking = trackActivity();

    const check = () => {
      if (idleTimeout.current <= 0) return;
      const left = Math.ceil((idleTimeout.current * 1000 - (Date.now() - lastActivity())) / 1000);
      if (left <= 0) {
        endForIdle();
        return;
      }
      const warning = left <= IDLE_WARNING_SECONDS ? left : null;
      setState((prev) => (prev.idleSecondsLeft === warning ? prev : { ...prev, idleSecondsLeft: warning }));
    };
    const interval = window.setInterval(check, IDLE_CHECK_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    check();

    return () => {
      stopTracking();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [signedIn, endForIdle]);

  const signIn = useCallback(
    async (userId: string, password: string, pcc: string) => {
      const result = await api.login(userId, password, pcc);
      if (!api.isMfaChallenge(result)) applySession(result);
      return result;
    },
    [applySession],
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, code: string) => {
      const session = await api.loginMfa(mfaToken, code);
      applySession(session);
      return session;
    },
    [applySession],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      clearRenewTimer();
      setAccessToken(null);
      setState(anonymousState);
    }
  }, [clearRenewTimer]);

  const refreshUser = useCallback(async () => {
    const { user, pcc, mustChangePassword } = await api.fetchSession();
    setState((prev) => ({
      ...prev,
      status: 'authenticated',
      user,
      pcc: pcc ?? user.pcc ?? '',
      mustChangePassword,
    }));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, verifyMfa, signOut, applySession, refreshUser }),
    [state, signIn, verifyMfa, signOut, applySession, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return ctx;
}
